import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Users,
  UserPlus,
  CalendarDays,
  CreditCard,
  DollarSign,
  TrendingDown,
} from "lucide-react";

const MRR_KEY = "td_launch_mrr_price";

function startOfDay(d = new Date()) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function daysAgo(n: number) {
  const d = startOfDay();
  d.setDate(d.getDate() - n);
  return d;
}

export function LaunchTab() {
  const [loading, setLoading] = useState(true);
  const [pricePerSub, setPricePerSub] = useState<number>(() => {
    const raw = Number(localStorage.getItem(MRR_KEY));
    return Number.isFinite(raw) && raw > 0 ? raw : 29;
  });

  const [totals, setTotals] = useState({
    totalUsers: 0,
    signupsToday: 0,
    signupsWeek: 0,
    activeSubs: 0,
    canceled30d: 0,
    activeStart30d: 0,
  });
  const [growth, setGrowth] = useState<{ date: string; users: number }[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const todayStart = startOfDay().toISOString();
      const weekStart = daysAgo(6).toISOString();
      const thirtyStart = daysAgo(29).toISOString();

      const [
        usersTotal,
        signupsTodayRes,
        signupsWeekRes,
        signupsGrowthRes,
        activeSubsRes,
        canceledRes,
        activeStartRes,
      ] = await Promise.all([
        supabase.from("user_preferences").select("id", { count: "exact", head: true }),
        supabase
          .from("user_preferences")
          .select("id", { count: "exact", head: true })
          .gte("created_at", todayStart),
        supabase
          .from("user_preferences")
          .select("id", { count: "exact", head: true })
          .gte("created_at", weekStart),
        supabase
          .from("user_preferences")
          .select("created_at")
          .order("created_at", { ascending: true }),
        supabase
          .from("licenses")
          .select("id", { count: "exact", head: true })
          .eq("status", "active"),
        // churn proxy: licenses updated in last 30d with non-active status
        supabase
          .from("licenses")
          .select("id", { count: "exact", head: true })
          .neq("status", "active")
          .gte("updated_at", thirtyStart),
        // active subscribers at start of 30d window (created before then)
        supabase
          .from("licenses")
          .select("id", { count: "exact", head: true })
          .lte("created_at", thirtyStart),
      ]);

      if (cancelled) return;

      const allSignups =
        (signupsGrowthRes.data as { created_at: string }[] | null) ?? [];
      const totalBefore30 = allSignups.filter(
        (s) => new Date(s.created_at) < daysAgo(29)
      ).length;
      const buckets = new Map<string, number>();
      for (let i = 29; i >= 0; i--) {
        const d = daysAgo(i);
        buckets.set(d.toISOString().slice(0, 10), 0);
      }
      for (const s of allSignups) {
        const key = s.created_at.slice(0, 10);
        if (buckets.has(key)) buckets.set(key, (buckets.get(key) || 0) + 1);
      }
      let cumulative = totalBefore30;
      const series: { date: string; users: number }[] = [];
      for (const [date, count] of buckets.entries()) {
        cumulative += count;
        series.push({ date: date.slice(5), users: cumulative });
      }

      setTotals({
        totalUsers: usersTotal.count ?? 0,
        signupsToday: signupsTodayRes.count ?? 0,
        signupsWeek: signupsWeekRes.count ?? 0,
        activeSubs: activeSubsRes.count ?? 0,
        canceled30d: canceledRes.count ?? 0,
        activeStart30d: activeStartRes.count ?? 0,
      });
      setGrowth(series);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const mrr = useMemo(
    () => totals.activeSubs * pricePerSub,
    [totals.activeSubs, pricePerSub]
  );

  const churnRate = useMemo(() => {
    if (totals.activeStart30d === 0) return 0;
    return Math.round((totals.canceled30d / totals.activeStart30d) * 1000) / 10;
  }, [totals.canceled30d, totals.activeStart30d]);

  const conversionRate = useMemo(() => {
    if (totals.totalUsers === 0) return 0;
    return Math.round((totals.activeSubs / totals.totalUsers) * 1000) / 10;
  }, [totals.totalUsers, totals.activeSubs]);

  const updatePrice = (v: number) => {
    setPricePerSub(v);
    localStorage.setItem(MRR_KEY, String(v));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Launch command center</h2>
          <p className="text-sm text-muted-foreground">
            Your first 30 days at a glance. Live numbers from auth, onboarding, and
            billing.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="font-mono text-[10px] uppercase text-muted-foreground">
              Avg. price / sub / mo (USD)
            </Label>
            <Input
              type="number"
              min={1}
              step={1}
              value={pricePerSub}
              onChange={(e) => updatePrice(Number(e.target.value) || 0)}
              className="h-9 w-32 font-mono"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard
          icon={Users}
          label="Total users"
          value={totals.totalUsers}
          sub="onboarded"
        />
        <MetricCard
          icon={UserPlus}
          label="Sign-ups today"
          value={totals.signupsToday}
          sub="since midnight"
        />
        <MetricCard
          icon={CalendarDays}
          label="Sign-ups (7d)"
          value={totals.signupsWeek}
          sub="rolling week"
        />
        <MetricCard
          icon={CreditCard}
          label="Active subs"
          value={totals.activeSubs}
          sub={`${conversionRate}% of users`}
        />
        <MetricCard
          icon={DollarSign}
          label="MRR"
          value={`$${mrr.toLocaleString()}`}
          sub={`${totals.activeSubs} × $${pricePerSub}`}
        />
        <MetricCard
          icon={TrendingDown}
          label="Churn (30d)"
          value={`${churnRate}%`}
          sub={`${totals.canceled30d} canceled`}
          tone={churnRate > 5 ? "alert" : "default"}
        />
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold">User growth — last 30 days</h3>
          <span className="font-mono text-[10px] text-muted-foreground">
            {loading ? "loading…" : `${totals.totalUsers} cumulative`}
          </span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={growth} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="g-launch-users" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <RTooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  fontSize: 12,
                }}
              />
              <Area
                type="monotone"
                dataKey="users"
                stroke="hsl(var(--primary))"
                fill="url(#g-launch-users)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        <div className="font-mono text-xs text-primary mb-2">// HOW THIS IS CALCULATED</div>
        <ul className="space-y-1 list-disc pl-5">
          <li>
            <strong>Total users</strong>: rows in <code>user_preferences</code> (onboarded
            accounts).
          </li>
          <li>
            <strong>Active subs</strong>: licenses with <code>status = active</code>.
          </li>
          <li>
            <strong>MRR</strong>: active subs × your average monthly price (editable
            above). For per-customer billing accuracy, plug Stripe in later.
          </li>
          <li>
            <strong>Churn (30d)</strong>: licenses moved off <code>active</code> in the
            last 30 days divided by subs that existed at the start of the window.
          </li>
        </ul>
      </div>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  sub,
  tone = "default",
}: {
  icon: typeof Users;
  label: string;
  value: number | string;
  sub?: string;
  tone?: "default" | "alert";
}) {
  return (
    <div
      className={`rounded-xl border bg-card p-4 ${
        tone === "alert" ? "border-accent/60" : "border-border"
      }`}
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="size-3.5" />
        <span className="font-mono text-[10px] uppercase">{label}</span>
      </div>
      <div
        className={`mt-2 font-display text-2xl font-bold ${
          tone === "alert" ? "text-accent" : ""
        }`}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
